import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { MobileNavigation } from '../components/MobileNavigation';
import { 
  ChartLine, 
  Users, 
  CurrencyDollar, 
  CalendarCheck, 
  Warning, 
  FileText,
  Export,
  ArrowUp,
  ArrowDown,
  Clock,
  MapPin,
  CaretLeft,
  CaretRight,
  Funnel,
  X
} from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/api';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6'];

export function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [attendanceChart, setAttendanceChart] = useState([]);
  const [capacityByLocation, setCapacityByLocation] = useState([]);
  const [waiverTrend, setWaiverTrend] = useState(null);
  const [creditsChart, setCreditsChart] = useState([]);
  const [sessionPerformance, setSessionPerformance] = useState({ sessions: [], pagination: {} });
  const [playerParticipation, setPlayerParticipation] = useState({ players: [], pagination: {} });
  const [paymentsTable, setPaymentsTable] = useState({ payments: [], pagination: {} });
  const [alerts, setAlerts] = useState({ alerts: [], total: 0 });
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    locationIds: [],
    ageGroup: '',
    period: 'daily'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Table pagination
  const [sessionPage, setSessionPage] = useState(1);
  const [playerPage, setPlayerPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [playerSearch, setPlayerSearch] = useState('');

  const fetchKPIs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.locationIds.length > 0) params.append('location_ids', filters.locationIds.join(','));
      if (filters.ageGroup) params.append('age_group', filters.ageGroup);
      
      const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics/kpis?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setKpis(data);
      }
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  }, [filters]);

  const fetchChartData = useCallback(async () => {
    try {
      // Revenue chart
      const revenueParams = new URLSearchParams();
      revenueParams.append('period', filters.period);
      if (filters.startDate) revenueParams.append('start_date', filters.startDate);
      if (filters.endDate) revenueParams.append('end_date', filters.endDate);
      
      const revenueRes = await fetch(`${getApiBaseUrl()}/api/admin/analytics/revenue-chart?${revenueParams}`, {
        credentials: 'include'
      });
      if (revenueRes.ok) {
        const data = await revenueRes.json();
        setRevenueChart(data.data);
      }
      
      // Attendance chart
      const attendanceParams = new URLSearchParams();
      if (filters.startDate) attendanceParams.append('start_date', filters.startDate);
      if (filters.endDate) attendanceParams.append('end_date', filters.endDate);
      if (filters.locationIds.length > 0) attendanceParams.append('location_ids', filters.locationIds.join(','));
      
      const attendanceRes = await fetch(`${getApiBaseUrl()}/api/admin/analytics/attendance-chart?${attendanceParams}`, {
        credentials: 'include'
      });
      if (attendanceRes.ok) {
        const data = await attendanceRes.json();
        setAttendanceChart(data.data);
      }
      
      // Capacity by location
      const capacityRes = await fetch(`${getApiBaseUrl()}/api/admin/analytics/capacity-by-location?${attendanceParams}`, {
        credentials: 'include'
      });
      if (capacityRes.ok) {
        const data = await capacityRes.json();
        setCapacityByLocation(data.locations);
      }
      
      // Waiver trend
      const waiverRes = await fetch(`${getApiBaseUrl()}/api/admin/analytics/waiver-trend`, {
        credentials: 'include'
      });
      if (waiverRes.ok) {
        const data = await waiverRes.json();
        setWaiverTrend(data);
      }
      
      // Credits chart
      const creditsRes = await fetch(`${getApiBaseUrl()}/api/admin/analytics/credits-chart`, {
        credentials: 'include'
      });
      if (creditsRes.ok) {
        const data = await creditsRes.json();
        setCreditsChart(data.data);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  }, [filters]);

  const fetchSessionPerformance = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', sessionPage);
      params.append('limit', '10');
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics/session-performance?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessionPerformance(data);
      }
    } catch (error) {
      console.error('Error fetching session performance:', error);
    }
  }, [sessionPage, filters.startDate, filters.endDate]);

  const fetchPlayerParticipation = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', playerPage);
      params.append('limit', '10');
      if (playerSearch) params.append('search', playerSearch);
      
      const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics/player-participation?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPlayerParticipation(data);
      }
    } catch (error) {
      console.error('Error fetching player participation:', error);
    }
  }, [playerPage, playerSearch]);

  const fetchPaymentsTable = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', paymentPage);
      params.append('limit', '10');
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics/payments-table?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentsTable(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, [paymentPage, filters.startDate, filters.endDate]);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics/alerts-detailed`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/locations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchLocations(),
        fetchKPIs(),
        fetchChartData(),
        fetchAlerts()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchKPIs, fetchChartData, fetchAlerts]);

  useEffect(() => {
    if (!loading) {
      fetchSessionPerformance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionPage, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (!loading) {
      fetchPlayerParticipation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerPage, playerSearch]);

  useEffect(() => {
    if (!loading) {
      fetchPaymentsTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPage, filters.startDate, filters.endDate]);

  const handleExport = async (type) => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics/export/${type}?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} data exported successfully`);
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const applyFilters = () => {
    fetchKPIs();
    fetchChartData();
    fetchSessionPerformance();
    fetchPaymentsTable();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      locationIds: [],
      ageGroup: '',
      period: 'daily'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 overflow-x-hidden" data-testid="admin-analytics">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Insights</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">Analytics</h1>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={showFilters ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'btn-primary' : ''}
                data-testid="toggle-filters-btn"
              >
                <Funnel size={18} className="mr-1" />
                Filters
              </Button>
            </div>
          </div>
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-card rounded-lg border border-border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-sm">FILTERS</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Select
                    value={filters.locationIds[0] || 'all'}
                    onValueChange={(value) => setFilters({...filters, locationIds: value === 'all' ? [] : [value]})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.location_id} value={loc.location_id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Age Group</Label>
                  <Select
                    value={filters.ageGroup || 'all'}
                    onValueChange={(value) => setFilters({...filters, ageGroup: value === 'all' ? '' : value})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Ages" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ages</SelectItem>
                      <SelectItem value="9-11">Ages 9-11</SelectItem>
                      <SelectItem value="12-14">Ages 12-14</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={applyFilters} className="btn-primary" size="sm">
                  Apply Filters
                </Button>
                <Button onClick={clearFilters} variant="outline" size="sm">
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-8 space-y-8 stagger-children">
        {/* KPI Cards - Premium Grid */}
        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="stats-card">
              <div className="icon-container mb-3 text-green-500">
                <CurrencyDollar size={20} weight="duotone" />
              </div>
              <div className="text-2xl sm:text-3xl font-black text-green-500">${kpis.revenue.mtd.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">MTD Revenue</div>
              <div className="text-xs text-muted-foreground">YTD: ${kpis.revenue.ytd.toLocaleString()}</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-blue-500">
                <CurrencyDollar size={20} weight="duotone" />
              </div>
              <div className="text-2xl sm:text-3xl font-black text-blue-500">${kpis.revenue.per_session}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Per Session</div>
              <div className="text-xs text-muted-foreground">{kpis.capacity.sessions_count} sessions</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-primary">
                <Users size={20} weight="duotone" />
              </div>
              <div className="text-2xl sm:text-3xl font-black text-primary">{kpis.registrations.mtd}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Registrations</div>
              <div className="text-xs text-muted-foreground">Total: {kpis.registrations.total_registered}</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-purple-500">
                <CalendarCheck size={20} weight="duotone" />
              </div>
              <div className="text-2xl sm:text-3xl font-black text-purple-500">{kpis.attendance.rate}%</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Attendance</div>
              <div className="text-xs text-muted-foreground">{kpis.attendance.total_checked_in} checked in</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-yellow-500">
                <ChartLine size={20} weight="duotone" />
              </div>
              <div className="text-2xl sm:text-3xl font-black text-yellow-500">{kpis.capacity.utilization}%</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Utilization</div>
              <div className="text-xs text-muted-foreground">{kpis.attendance.avg_per_session} avg/session</div>
            </div>
          </div>
        )}
        
        {/* Secondary KPIs */}
        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="stats-card">
              <div className="icon-container mb-3 text-red-500">
                <Warning size={20} weight="duotone" />
              </div>
              <div className="text-2xl font-black text-red-500">{kpis.attendance.no_show_rate}%</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">No-Show Rate</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-teal-500">
                <FileText size={20} weight="duotone" />
              </div>
              <div className="text-2xl font-black text-teal-500">{kpis.waivers.signed}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Waivers Signed</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-cyan-500">
                <Users size={20} weight="duotone" />
              </div>
              <div className="text-2xl font-black text-cyan-500">{kpis.players?.total || 0}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Total Players</div>
            </div>
            
            <div className="stats-card">
              <div className="icon-container mb-3 text-amber-500">
                <CurrencyDollar size={20} weight="duotone" />
              </div>
              <div className="text-2xl font-black text-amber-500">${kpis.credits?.outstanding || 0}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">Credits Out</div>
            </div>
          </div>
        )}

        {/* Alerts Section */}
        {alerts.total > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Warning size={20} className="text-yellow-500" />
                ALERTS & INSIGHTS
                <Badge variant="secondary" className="ml-2">{alerts.total}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {alerts.alerts.slice(0, 5).map((alert, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      alert.severity === 'critical' ? 'border-red-500 bg-red-500/10' :
                      alert.severity === 'warning' ? 'border-yellow-500 bg-yellow-500/10' :
                      'border-blue-500 bg-blue-500/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-sm">{alert.title}</div>
                        <div className="text-xs text-muted-foreground">{alert.message}</div>
                      </div>
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                        {alert.severity}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts Section */}
        <Tabs defaultValue="revenue" className="w-full">
          <div className="overflow-x-auto pb-2">
            <TabsList className="inline-flex w-max sm:w-full sm:grid sm:grid-cols-3 lg:grid-cols-6 gap-1">
              <TabsTrigger value="revenue" className="text-xs sm:text-sm whitespace-nowrap px-3">Revenue</TabsTrigger>
              <TabsTrigger value="attendance" className="text-xs sm:text-sm whitespace-nowrap px-3">Attendance</TabsTrigger>
              <TabsTrigger value="capacity" className="text-xs sm:text-sm whitespace-nowrap px-3">Capacity</TabsTrigger>
              <TabsTrigger value="waivers" className="text-xs sm:text-sm whitespace-nowrap px-3">Waivers</TabsTrigger>
              <TabsTrigger value="credits" className="text-xs sm:text-sm whitespace-nowrap px-3">Credits</TabsTrigger>
              <TabsTrigger value="top" className="text-xs sm:text-sm whitespace-nowrap px-3">Top</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="revenue" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">REVENUE OVER TIME</CardTitle>
                  <Select
                    value={filters.period}
                    onValueChange={(value) => {
                      setFilters({...filters, period: value});
                      setTimeout(fetchChartData, 100);
                    }}
                  >
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-56 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="period" stroke="#888" fontSize={10} tick={{fontSize: 10}} />
                      <YAxis stroke="#888" fontSize={10} tick={{fontSize: 10}} width={40} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                        formatter={(value) => [`$${value}`, 'Revenue']}
                      />
                      <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="attendance" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading">REGISTRATIONS VS CHECK-INS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={attendanceChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#888" fontSize={10} tick={{fontSize: 10}} />
                      <YAxis stroke="#888" fontSize={10} tick={{fontSize: 10}} width={30} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                      <Legend wrapperStyle={{fontSize: '12px'}} />
                      <Bar dataKey="registered" fill="#3b82f6" name="Registered" />
                      <Bar dataKey="checked_in" fill="#22c55e" name="Checked In" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="capacity" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading">CAPACITY UTILIZATION BY LOCATION</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56 sm:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={capacityByLocation} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" domain={[0, 100]} stroke="#888" fontSize={10} tick={{fontSize: 10}} />
                      <YAxis dataKey="name" type="category" stroke="#888" fontSize={10} width={80} tick={{fontSize: 10}} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                        formatter={(value) => [`${value}%`, 'Utilization']}
                      />
                      <Bar dataKey="utilization" fill="#f97316" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="waivers" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading">WAIVER COMPLIANCE</CardTitle>
              </CardHeader>
              <CardContent>
                {waiverTrend && (
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="h-64 flex-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Valid', value: waiverTrend.valid_waivers },
                              { name: 'Expiring (30d)', value: waiverTrend.expiring_30d },
                              { name: 'Expired', value: waiverTrend.expired },
                              { name: 'Missing', value: waiverTrend.missing }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {[0, 1, 2, 3].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      <div className="text-center p-4 bg-green-500/10 rounded-lg">
                        <div className="text-3xl font-heading text-green-500">{waiverTrend.compliance_rate}%</div>
                        <div className="text-xs text-muted-foreground">Compliance Rate</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-card rounded border border-border">
                          <div className="font-medium">{waiverTrend.expiring_14d}</div>
                          <div className="text-xs text-muted-foreground">Expiring in 14d</div>
                        </div>
                        <div className="p-2 bg-card rounded border border-border">
                          <div className="font-medium">{waiverTrend.expiring_3d}</div>
                          <div className="text-xs text-muted-foreground">Expiring in 3d</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="credits" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading">CREDITS ISSUED VS USED</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={creditsChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#888" fontSize={12} />
                      <YAxis stroke="#888" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                        formatter={(value) => [`$${value}`, '']}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="issued" stroke="#3b82f6" strokeWidth={2} name="Issued" />
                      <Line type="monotone" dataKey="used" stroke="#22c55e" strokeWidth={2} name="Used" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="top" className="mt-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading">TOP SESSIONS BY ATTENDANCE</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sessionPerformance.sessions.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#888" fontSize={12} />
                      <YAxis stroke="#888" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                      <Bar dataKey="checked_in" fill="#f97316" name="Checked In" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Tables Section */}
        <Tabs defaultValue="sessions" className="w-full">
          <div className="space-y-3 mb-4">
            <div className="overflow-x-auto -mx-4 px-4">
              <TabsList className="inline-flex w-auto min-w-max">
                <TabsTrigger value="sessions" className="text-xs sm:text-sm px-3">Sessions</TabsTrigger>
                <TabsTrigger value="players" className="text-xs sm:text-sm px-3">Players</TabsTrigger>
                <TabsTrigger value="payments" className="text-xs sm:text-sm px-3">Payments</TabsTrigger>
              </TabsList>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('sessions')} className="text-xs justify-center">
                <Export size={14} className="mr-1" /> Sessions
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('attendance')} className="text-xs justify-center">
                <Export size={14} className="mr-1" /> Attendance
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('revenue')} className="text-xs justify-center">
                <Export size={14} className="mr-1" /> Revenue
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('credits')} className="text-xs justify-center">
                <Export size={14} className="mr-1" /> Credits
              </Button>
            </div>
          </div>
          
          <TabsContent value="sessions">
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 font-heading">Date</th>
                        <th className="text-left p-3 font-heading">Location</th>
                        <th className="text-center p-3 font-heading">Cap</th>
                        <th className="text-center p-3 font-heading">Reg</th>
                        <th className="text-center p-3 font-heading">In</th>
                        <th className="text-center p-3 font-heading">Util%</th>
                        <th className="text-right p-3 font-heading">Revenue</th>
                        <th className="text-center p-3 font-heading">No-Shows</th>
                        <th className="text-center p-3 font-heading">Waivers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionPerformance.sessions.map((session) => (
                        <tr key={session.session_id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium">{session.date}</div>
                            <div className="text-xs text-muted-foreground">{session.start_time}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <MapPin size={14} className="text-muted-foreground" />
                              {session.location}
                            </div>
                            <Badge variant="secondary" className="text-xs mt-1">{session.age_group}</Badge>
                          </td>
                          <td className="text-center p-3">{session.capacity}</td>
                          <td className="text-center p-3">{session.registered}</td>
                          <td className="text-center p-3">{session.checked_in}</td>
                          <td className="text-center p-3">
                            <span className={session.utilization >= 60 ? 'text-green-500' : 'text-yellow-500'}>
                              {session.utilization}%
                            </span>
                          </td>
                          <td className="text-right p-3">${session.revenue}</td>
                          <td className="text-center p-3">
                            {session.no_shows > 0 ? (
                              <span className="text-red-500">{session.no_shows}</span>
                            ) : (
                              <span className="text-green-500">0</span>
                            )}
                          </td>
                          <td className="text-center p-3">
                            {session.waivers_missing > 0 ? (
                              <Badge variant="destructive">{session.waivers_missing} missing</Badge>
                            ) : (
                              <Badge variant="secondary">OK</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sessionPerformance.pagination.pages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Page {sessionPerformance.pagination.page} of {sessionPerformance.pagination.pages}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={sessionPage === 1}
                        onClick={() => setSessionPage(p => p - 1)}
                      >
                        <CaretLeft size={16} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={sessionPage >= sessionPerformance.pagination.pages}
                        onClick={() => setSessionPage(p => p + 1)}
                      >
                        <CaretRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="players">
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-4">
                  <Input
                    placeholder="Search players..."
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    className="max-w-xs h-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 font-heading">Player</th>
                        <th className="text-left p-3 font-heading">Parent</th>
                        <th className="text-center p-3 font-heading">Attendance</th>
                        <th className="text-left p-3 font-heading">Last Check-in</th>
                        <th className="text-center p-3 font-heading">Waiver</th>
                        <th className="text-right p-3 font-heading">Credits</th>
                        <th className="text-center p-3 font-heading">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playerParticipation.players.map((player) => (
                        <tr key={player.player_id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium">{player.name}</div>
                            <div className="text-xs text-muted-foreground">Age {player.age} ({player.age_group})</div>
                          </td>
                          <td className="p-3">
                            <div>{player.parent_name}</div>
                            <div className="text-xs text-muted-foreground">{player.parent_email}</div>
                          </td>
                          <td className="text-center p-3 font-medium">{player.attendance_count}</td>
                          <td className="p-3 text-sm">
                            {player.last_checkin ? new Date(player.last_checkin).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="text-center p-3">
                            <Badge variant={
                              player.waiver_status === 'valid' ? 'secondary' :
                              player.waiver_status === 'expired' ? 'destructive' : 'outline'
                            }>
                              {player.waiver_status}
                            </Badge>
                          </td>
                          <td className="text-right p-3">${player.credits_balance}</td>
                          <td className="text-center p-3">
                            {player.flags.length > 0 ? (
                              <div className="flex gap-1 justify-center">
                                {player.flags.map((flag, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {flag.replace('_', ' ')}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {playerParticipation.pagination.pages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Page {playerParticipation.pagination.page} of {playerParticipation.pagination.pages}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={playerPage === 1}
                        onClick={() => setPlayerPage(p => p - 1)}
                      >
                        <CaretLeft size={16} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={playerPage >= playerParticipation.pagination.pages}
                        onClick={() => setPlayerPage(p => p + 1)}
                      >
                        <CaretRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="payments">
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 font-heading">Date</th>
                        <th className="text-left p-3 font-heading">Payer</th>
                        <th className="text-left p-3 font-heading">Player</th>
                        <th className="text-left p-3 font-heading">Session</th>
                        <th className="text-right p-3 font-heading">Amount</th>
                        <th className="text-right p-3 font-heading">Credits</th>
                        <th className="text-center p-3 font-heading">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsTable.payments.map((payment) => (
                        <tr key={payment.payment_id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            {payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3">
                            <div>{payment.payer_name}</div>
                            <div className="text-xs text-muted-foreground">{payment.payer_email}</div>
                          </td>
                          <td className="p-3">{payment.player_name}</td>
                          <td className="p-3">{payment.session_date || '-'}</td>
                          <td className="text-right p-3 font-medium">${payment.amount}</td>
                          <td className="text-right p-3">
                            {payment.credits_applied > 0 ? (
                              <span className="text-green-500">-${payment.credits_applied}</span>
                            ) : '-'}
                          </td>
                          <td className="text-center p-3">
                            <Badge variant={
                              payment.status === 'paid' ? 'secondary' :
                              payment.status === 'pending' ? 'outline' : 'destructive'
                            }>
                              {payment.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {paymentsTable.pagination.pages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-border">
                    <div className="text-sm text-muted-foreground">
                      Page {paymentsTable.pagination.page} of {paymentsTable.pagination.pages}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={paymentPage === 1}
                        onClick={() => setPaymentPage(p => p - 1)}
                      >
                        <CaretLeft size={16} />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={paymentPage >= paymentsTable.pagination.pages}
                        onClick={() => setPaymentPage(p => p + 1)}
                      >
                        <CaretRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <MobileNavigation userRole="admin" />
    </div>
  );
}
